import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import { workspacePickersAsUsers } from '@/api/services/workspaceUsers';
import { ToggleField } from '@/components/ui';
import type { SearchShare, User } from '@/types';

type Props = {
  searchId: string;
  searchOwnerId?: string | null;
  currentUserId?: string | null;
  canManageShares: boolean;
};

function displayUser(user: User): string {
  return user.name?.trim() || user.email || user.id;
}

export default function SearchSharePanel({
  searchId,
  searchOwnerId,
  currentUserId,
  canManageShares,
}: Props) {
  const [shares, setShares] = useState<SearchShare[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [granteeId, setGranteeId] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [canManageNotifications, setCanManageNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEditShares = Boolean(
    canManageShares && searchOwnerId && (currentUserId === searchOwnerId || canManageShares)
  );

  const load = useCallback(async () => {
    if (!canEditShares) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [shareRows, pickers] = await Promise.all([
        api.searches.listShares(searchId),
        api.workspaceUsers.pickers(),
      ]);
      setShares(shareRows);
      setUsers(
        workspacePickersAsUsers(pickers.items).filter(
          (u) => u.id !== searchOwnerId && u.id !== currentUserId && u.is_enabled !== false
        )
      );
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load shares');
    } finally {
      setLoading(false);
    }
  }, [canEditShares, currentUserId, searchId, searchOwnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canEditShares) {
    return null;
  }

  async function addShare(e: React.FormEvent) {
    e.preventDefault();
    if (!granteeId) return;
    setSaving(true);
    setError('');
    try {
      await api.searches.upsertShare(searchId, {
        grantee_user_id: granteeId,
        can_view: true,
        can_edit: canEdit,
        can_manage_notifications: canManageNotifications,
      });
      setGranteeId('');
      setCanEdit(false);
      setCanManageNotifications(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save share');
    } finally {
      setSaving(false);
    }
  }

  async function removeShare(granteeUserId: string) {
    setSaving(true);
    setError('');
    try {
      await api.searches.deleteShare(searchId, granteeUserId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove share');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="search-share-panel">
      <h4 className="search-share-panel__title">Share with team</h4>
      <p className="muted search-share-panel__hint">
        Grant view, edit, or notification management without transferring ownership.
      </p>
      {error ? <div className="error">{error}</div> : null}
      {loading ? <p className="muted">Loading shares…</p> : null}
      {!loading && shares.length > 0 ? (
        <ul className="search-share-panel__list">
          {shares.map((share) => (
            <li key={share.id}>
              <span>{share.grantee_name || share.grantee_email || share.grantee_user_id}</span>
              <span className="muted">
                {[
                  share.can_view ? 'view' : null,
                  share.can_edit ? 'edit' : null,
                  share.can_manage_notifications ? 'notifications' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={saving}
                onClick={() => void removeShare(share.grantee_user_id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!loading ? (
        <form className="search-share-panel__form" onSubmit={(e) => void addShare(e)}>
          <label htmlFor="share-grantee">Team member</label>
          <select
            id="share-grantee"
            className="filter-select"
            value={granteeId}
            onChange={(e) => setGranteeId(e.target.value)}
          >
            <option value="">Select user…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {displayUser(u)} ({u.role})
              </option>
            ))}
          </select>
          <ToggleField compact checked={canEdit} onChange={setCanEdit} label="Allow edit" />
          <ToggleField
            compact
            checked={canManageNotifications}
            onChange={setCanManageNotifications}
            label="Manage notifications"
          />
          <button type="submit" className="btn btn-sm" disabled={saving || !granteeId}>
            {saving ? 'Saving…' : 'Share'}
          </button>
        </form>
      ) : null}
    </div>
  );
}
